import React, { useEffect, useRef } from 'react'
import { init, getInstanceByDom } from 'echarts'
import { engineStateLabel } from '../../../constants/engine-states'

// Continuous step-line of optimus_vehicle_state over time. The graph spans the step's criteria
// window plus an overflow buffer (e.g. 30 min in state + 5 min = 35 min). It opens on the lookback
// leading into the test, with a "Test start" marker where grading begins, fills left-to-right, and
// once the span is full it scrolls so the marker works its way left and eventually drops off. The
// x-axis reads minutes relative to the test start (negative = lookback). The y-axis is labeled with
// state names and the tooltip shows the state, so a single-color line stays readable. Uses ECharts
// imperatively (init/dispose on mount, setOption on data change) — the same approach as the optimus
// sparkline.
const StateGraph = ({ series = [], windowSeconds = 300, bufferSeconds = 300, windowStartedAt = null }) => {
    const chartRef = useRef(null)

    useEffect(() => {
        const chart = init(chartRef.current)
        return () => chart?.dispose()
    }, [])

    useEffect(() => {
        const chart = getInstanceByDom(chartRef.current)
        if (!chart) {
            return
        }

        // Axis labels are relative to the test start; fall back to the first sample before it's set.
        const origin = windowStartedAt || (series.length ? series[0].t : 0)
        const span = windowSeconds + bufferSeconds
        const first = series.length ? series[0].t : origin
        const latest = series.length ? series[series.length - 1].t : origin
        // Scroll only once the span is full — until then the line grows into a fixed span.
        const min = Math.max(first, latest - span)
        const max = min + span
        const data = series.map(point => [point.t, point.v])

        chart.setOption({
            grid: { top: 10, bottom: 26, left: 120, right: 12 },
            tooltip: {
                trigger: 'axis',
                formatter: (params) => {
                    const p = params[0]
                    const time = new Date(p.value[0] * 1000).toLocaleTimeString()
                    return `${time} — ${engineStateLabel(p.value[1])}`
                }
            },
            xAxis: {
                type: 'value',
                min,
                max,
                axisLabel: { formatter: (val) => `${Math.round((val - origin) / 60)}m` }
            },
            yAxis: {
                type: 'value',
                min: 0,
                max: 7,
                interval: 1,
                axisLabel: { formatter: (val) => engineStateLabel(val) }
            },
            series: [{
                type: 'line',
                step: 'end',
                data,
                showSymbol: false,
                animation: false,
                connectNulls: true,
                lineStyle: { width: 2, color: '#0A6AAD' },
                itemStyle: { color: '#0A6AAD' },
                // Only drawn while the start is still inside the visible span.
                markLine: windowStartedAt && windowStartedAt >= min ? {
                    symbol: 'none',
                    silent: true,
                    label: { formatter: 'Test start', position: 'insideEndTop' },
                    lineStyle: { color: '#E67E22', type: 'dashed', width: 1.5 },
                    data: [{ xAxis: windowStartedAt }]
                } : undefined
            }]
        })
    }, [series, windowSeconds, bufferSeconds, windowStartedAt])

    return <div ref={chartRef} style={{ width: '100%', height: '170px' }} />
}

export default StateGraph
